import moment from 'moment';
import React, { Fragment, useMemo } from 'react';
import { SvgDot, SvgImdb, SvgPin } from '../../../icons';
import { Asset } from '../../../queries/ats/titles/types/asset';
import { Source } from '../../../queries/ats/titles/types/sources';
import { localizedRelativeTime } from '../../../utils/ats-details-utils';
import { classes, invariant, nonNullable } from '../../../utils/commonUtils';
import css from './info-line.module.scss';
import { ageColor, AgeLimitColors } from './utils';

interface InfoLineComponentProps {
  asset: Asset;
  source: Source;
  className?: string;
}

type InfoLineCaption = 'age' | 'runtime' | 'airtime' | 'seasons' | 'episodes' | 'year' | 'genres' | 'rating' | 'pin';

interface InfoLineProps {
  asset: Asset;
  source: Source;
  captions: readonly InfoLineCaption[];
  omitDotAtIndices?: number[];
  className?: string;
}

function mapCaptionToComponent(caption: InfoLineCaption, asset: Asset, source: Source) {
  switch (caption) {
    case 'age':
      if (asset.ageRating === null) {
        return null;
      }
      return <AgeRating value={asset.ageRating === 0 ? 'A' : asset.ageRating.toString()} color={ageColor(asset)} />;
    case 'runtime':
      return (
        <span className={css.caption}>
          {asset.duration ? `${Math.round(asset.duration / 60)} min` : 'unknown duration'}
        </span>
      );
    case 'airtime':
      invariant(
        source.type === 'program' || source.type === 'thirdparty',
        'Playback option must be a program or thirdparty when using the airtime caption',
      );

      const now = moment();
      const startTime = source.type === 'program' ? moment(source.startTime) : moment(source.availableFrom);
      const endTime = source.type === 'program' ? moment(source.endTime) : moment(source.availableTo);
      return (
        <span className={css.caption} data-highlight={now.isBetween(startTime, endTime)}>
          {localizedRelativeTime(startTime.format(), 'airtime')}
        </span>
      );
    case 'seasons':
      invariant(
        asset.type === 'series' || asset.type === 'episode',
        `Asset type must be "'series' | 'episode'" when using the seasons caption`,
      );
      return <span className={css.caption}>{asset.series.seasonCount} seasons</span>;
    case 'episodes':
      invariant(
        asset.type === 'series' || asset.type === 'episode',
        `Asset type must be "'series' | 'episode'" when using the episodes caption`,
      );
      return <span className={css.caption}>{asset.series.episodeCount} episodes</span>;
    case 'year':
      return asset.productionYear ? asset.productionYear.toString() : null;
    case 'genres':
      const genres = asset.genres
        .slice(0, 1)
        .map((x) => x.replace(/\b\w/g, (c) => c.toUpperCase()))
        .join(', ');
      return <span className={css.caption}>{genres}</span>;
    case 'rating':
      const rating = asset.scoreRating?.imdb;
      return rating ? <ScoreRating rating={rating} /> : null;
    case 'pin':
      if (
        source.type === 'program' &&
        source.recordings[0] !== undefined &&
        source.recordings[0].deleteMode === 'manual'
      ) {
        return <SvgPin width={18} height={18} />;
      }

      return null;
    default:
      return null;
  }
}

export function InfoLine({ asset, source, captions, omitDotAtIndices, className }: InfoLineProps) {
  const components = captions
    .map((caption) => {
      try {
        return mapCaptionToComponent(caption, asset, source);
      } catch {
        return null;
      }
    })
    .filter(nonNullable);

  return (
    <div className={classes(css.line, className)}>
      {components.map((component, idx) => {
        const omitDot = idx === components.length - 1 || omitDotAtIndices?.includes(idx);

        return (
          <Fragment key={idx}>
            {typeof component === 'string' ? <span className={css.caption}>{component}</span> : component}
            {!omitDot && <SvgDot />}
          </Fragment>
        );
      })}
    </div>
  );
}

export function TitleInfoLine({ asset, source, className }: InfoLineComponentProps) {
  const captions = useMemo(() => {
    switch (asset.type) {
      case 'movie':
        return ['age', 'runtime', 'year', 'genres', 'rating', 'pin'] as const;
      case 'series':
        return ['age', 'seasons', 'year', 'genres', 'rating', 'pin'] as const;
      case 'program':
        if (source.type !== 'program') {
          return ['age', 'runtime', 'year', 'genres', 'rating', 'pin'] as const;
        }

        return ['age', 'airtime', 'year', 'genres', 'rating', 'pin'] as const;
      default:
        return ['age', 'runtime', 'year', 'genres', 'rating', 'pin'] as const;
    }
  }, [asset.type, source.type]);

  return <InfoLine asset={asset} source={source} captions={captions} omitDotAtIndices={[0]} className={className} />;
}

export function InfoLineSkeleton() {
  return (
    <div className={classes(css.line, css.skeleton)}>
      <div className={css.age} />
      <div className={css.caption} />
      <div className={css.rating} />
    </div>
  );
}

InfoLine.Skeleton = InfoLineSkeleton;

interface AgeRatingProps {
  value: string;
  color: AgeLimitColors;
}

export function AgeRating({ value, color }: AgeRatingProps) {
  return <span className={classes(css.age, css[`age--${color}`])}>{value}</span>;
}

function ScoreRating({ rating }: { rating: number }) {
  return (
    <div className={css.rating}>
      <SvgImdb className={css.icon} />
      <span>{rating}</span>
    </div>
  );
}
